import { DEV } from "solid-js"
import { type BufferGeometry, type Material, type Mesh, type MeshStandardMaterial, Float32BufferAttribute } from "three"
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js"
import alwaysTrue from "../../ldsh/alwaysTrue"
import { appGltfLoader } from "./appGltfLoader"
import createSingleColorRGBFloat32Array from "./createSingleColorRGBFloat32Array"
import onGltfLoaderError from "./onGltfLoaderError"

export type MeshTestFn = (mesh: Mesh<BufferGeometry, Material>) => boolean

export default async function loadSingleModelAndMerge(
  model: string,
  scale: number = 1,
  meshTest: MeshTestFn = alwaysTrue,
): Promise<BufferGeometry> {
  return new Promise((resolve, reject) => {
    appGltfLoader.load(
      model,
      (gltf) => {
        try {
          const meshList = [
            ...gltf.scene.children[0].children as Array<Mesh<BufferGeometry, Material>>,
            ...gltf.scene.children as Array<Mesh<BufferGeometry, Material>>,
          ]
          
          const processedGeometries: BufferGeometry[] = []
          
          for (let i = 0; i < meshList.length; i++) {
            const mesh = meshList[i]
            
            // Skip if not a valid mesh with material
            if (!mesh.isMesh || !mesh.material.name || !meshTest(mesh)) {
              continue
            }
            if (DEV) {
              if (!('color' in mesh.material)) {
                throw new Error('Material does not have a color property')
              }
            }
            
            const geometry = mesh.geometry.clone()
            const color = (mesh.material as MeshStandardMaterial).color
            const colorArray = createSingleColorRGBFloat32Array(color, geometry.attributes.position.count)
            geometry.setAttribute('color', new Float32BufferAttribute(colorArray, 3))
            geometry.scale(scale, scale, scale)
            
            processedGeometries.push(geometry)
          }

          if (processedGeometries.length === 0) {
            if (DEV) {
              console.log('meshList', meshList, gltf)
            }
            throw new Error('No valid meshes with materials found in the model')
          }

          const mergedGeometry = mergeGeometries(processedGeometries)
          // mergedGeometry.computeVertexNormals() // Uncomment if needed
          resolve(mergedGeometry)
        } catch (error) {
          console.error('Error processing model:', error)
          reject(error)
        }
      },
      undefined,
      onGltfLoaderError
    )
  })
}